import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Eye, EyeOff, ImagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Panel, PageHead, Empty, StatusBadge } from "@/components/common";
import { ProductCard } from "@/components/ProductCard";
import { useApi, errMsg, inr } from "@/lib/api";
import { AdminPlans } from "@/components/Plans";

const ProductDialog = ({ open, product, defaultPrice, onClose, onSaved }) => {
  const api = useApi();
  const [form, setForm] = useState({ name: "", description: "", price: "", daily_return: 0, return_days: 0 });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setForm(product ? { name: product.name, description: product.description || "", price: product.price, daily_return: product.daily_return || 0, return_days: product.return_days || 0 } : { name: "", description: "", price: defaultPrice, daily_return: 0, return_days: 0 }); setFile(null); }
  }, [open, product, defaultPrice]);
  const save = async () => {
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("image", file);
    try { await api(product ? "put" : "post", product ? `/admin/products/${product.id}` : "/admin/products", fd); toast.success(product ? "Product updated" : "Product added"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#1F293D] bg-[#111827]" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{product ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <Input data-testid="product-name-input" placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Textarea data-testid="product-description-input" placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Input data-testid="product-price-input" type="number" placeholder="Price (₹)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="font-mono" />
        <div className="rounded-lg border border-[#1F293D] bg-[#0B0F17] p-3">
          <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">Daily income plan (leave 0 for none)</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-400">Income per day (₹)<Input data-testid="product-daily-return-input" type="number" min="0" value={form.daily_return} onChange={(e) => setForm({ ...form, daily_return: e.target.value })} className="mt-1 font-mono" /></label>
            <label className="text-xs text-slate-400">Number of days<Input data-testid="product-return-days-input" type="number" min="0" value={form.return_days} onChange={(e) => setForm({ ...form, return_days: e.target.value })} className="mt-1 font-mono" /></label>
          </div>
          {Number(form.daily_return) > 0 && Number(form.return_days) > 0 && <p className="mt-2 text-xs text-emerald-400" data-testid="product-plan-total">Member receives {inr(form.daily_return * form.return_days)} in total over {form.return_days} days.</p>}
        </div>
        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-[#1F293D] bg-[#0B0F17] px-4 py-3 text-sm text-slate-400 hover:border-emerald-500/50" data-testid="product-image-dropzone">
          {file ? <img src={URL.createObjectURL(file)} alt="" className="h-12 w-12 rounded object-cover" /> : <ImagePlus className="h-5 w-5 text-emerald-400" />}
          <span className="min-w-0 flex-1 truncate">{file ? file.name : product?.has_image ? "Replace product photo (optional)" : "Product photo (JPG/PNG/WEBP, max 5 MB)"}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="product-image-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size > 5242880) return toast.error("Image must be under 5 MB"); setFile(f || null); }} />
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="product-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy || form.name.trim().length < 2 || !(Number(form.price) > 0)} data-testid="product-save-btn">{busy ? "Saving…" : "Save product"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Products() {
  const api = useApi();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [defaultPrice, setDefaultPrice] = useState(2000);
  const load = useCallback(() => api("get", "/admin/products").then(setRows), [api]);
  useEffect(() => { load(); api("get", "/settings").then((s) => setDefaultPrice(s.package_price)).catch(() => {}); }, [load, api]);
  const toggle = async (p) => {
    try { const r = await api("post", `/admin/products/${p.id}/toggle`); toast.success(r.active ? "Product visible to members" : "Product hidden"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-products-page">
      <PageHead label="Admin" title="Activation products">
        <Button onClick={() => setEditing({})} data-testid="add-product-btn" className="rounded-full"><Plus className="mr-1 h-4 w-4" />Add product</Button>
      </PageHead>
      <p className="mb-6 max-w-2xl text-sm text-slate-400">Members pick one of these products to activate their ID and pay its price. While at least one product is visible, choosing a product and giving a delivery address is required.</p>
      <Panel>
        {!rows ? <p className="text-sm text-slate-500">Loading…</p> : rows.length === 0 ? <Empty testId="products-empty" text="No products yet. Add your first ₹2,000 activation product." /> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
            {rows.map((p) => (
              <ProductCard key={p.id} p={p}>
                <div className="mt-4 flex items-center justify-between">
                  <StatusBadge status={p.active ? "active" : "inactive"} testId={`product-status-${p.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`edit-product-btn-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => toggle(p)} data-testid={`toggle-product-btn-${p.id}`}>{p.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                  </div>
                </div>
              </ProductCard>
            ))}
          </div>
        )}
      </Panel>
      <AdminPlans />
      <ProductDialog open={!!editing} product={editing?.id ? editing : null} defaultPrice={defaultPrice} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
